import React, { useEffect, useState } from "react";
import { useQuery, useQueryClient } from "react-query";
import { useMatch } from "react-router-dom";

import styled from "styled-components";

import AiCursor from "components/atoms/AiCursor";
import Icon from "components/atoms/Icon";
import Alert from "components/atoms/alert/Alert";
import Button from "components/atoms/button";
import SelectBox from "components/atoms/input/SelectBox";
import useLoading from "components/atoms/loading/useLoading";
import Text from "components/atoms/text/Text";

import ReferenceLayer from "components/molecules/ReferenceLayer";

import { useSelectBoxOptions } from "hooks/queries/useOptions";
import { useResearchWidthHtmlCreate } from "hooks/queries/useResearch";

import { getConvertedPaper } from "utils/request/paper";
import { toBibJson } from "utils/scienceon";
import { sseUrl } from "utils/urls";
import { uuidv4 } from "utils/uuid";

const Container = styled.div`
    display: flex;
    flex-direction: column;
    width: 100%;
    height: 100%;
    min-height: 0;
`;

const ContentsWrap = styled.div`
    display: flex;
    flex: 1 1 auto;
    margin: 24px 0 0 0;
    min-height: 0;
`;

const ContentsSection = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    min-height: 0;
`;

const ItemView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    min-height: 0;
`;

const RelatedResearchView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    margin: 16px 0 0 0;
    width: 100%;
    min-height: 0;
    overflow: hidden;
`;

const TabSection = styled.div`
    display: flex;
    align-items: center;
`;

const TabButton = styled.button`
    position: relative;
    display: flex;
    justify-content: center;
    align-items: center;
    width: 120px;
    height: 40px;
    background-color: var(--color-White);
    border: Transparent;

    &:after {
        content: "";
        position: absolute;
        right: 0;
        bottom: 0;
        left: 0;
        width: 100%;
        height: 2px;
        background-color: ${(props) => (props.active ? "var(--color-Key)" : "var(--color-DisabledInput)")};
    }
`;

const TabButtonText = styled(Text).attrs((props) => ({
    variant: "body-md-400",
    color: props.isTabActive ? "var(--color-Key)" : "var(--color-DisabledText)",
}))``;

const TabViewSection = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    margin: 16px 0 0 0;
    width: 100%;
    min-height: 0;
    overflow: hidden;
`;

const RelResearchView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    position: relative;
    width: 100%;
    min-height: 0;
    overflow-y: hidden;
`;

const RelResearchViewFooter = styled.div`
    display: flex;
    justify-content: center;
    align-items: center;
    position: absolute;
    bottom: 0;
    left: 0;
    width: 100%;
    height: 50px;
    border-top: solid 1px var(--color-Line);
`;

const RelParagraphWrap = styled.div`
    flex: 1 1 auto;
    min-height: 0;
    width: 100%;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const RelParagraph = styled.div`
    margin: 0;
    padding: 4px;
    width: 100%;
    min-height: 100%;
    border: transparent;
    border-radius: 4px;
    background-color: var(--color-White);
    text-align: justify;
    line-height: 1.4;
    font-size: 13px;
    font-weight: 400;
    color: var(--color-SubBlack);
    resize: none;
    white-space: pre-wrap;

    &:focus {
        outline: none;
        background-color: var(--color-Base1);
    }

    &:disabled {
        cursor: text;
    }
`;

const ReferenceView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    position: relative;
    width: 100%;
    min-height: 0;
`;

const ButtonSection = styled.div`
    display: flex;
    align-items: center;
`;

const ReferenceListWrap = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    width: 100%;
    min-height: 0;
    overflow-y: auto;
    overflow-x: hidden;
    overscroll-behavior: contain;
    scrollbar-gutter: stable;
    padding-right: 4px;
`;

const RefListItemWrap = styled.div`
    padding: 8px 0;
    width: 100%;
    border-bottom: dashed 1px var(--color-Line);
`;

const AnalysisView = styled.div`
    display: flex;
    flex: 1 1 auto;
    flex-direction: column;
    justify-content: center;
    align-items: center;
    width: 100%;
    min-height: 0;
`;

const AnalysisDescText = styled(Text).attrs({
    variant: "body-md-400",
    margin: "0 0 20px 0",
})``;

const RefCitationText = styled(Text).attrs({
    variant: "body-md-400",
    color: "var(--color-Grey1)",
})``;

function ReferenceSummaryModal(props) {
    const match = useMatch("/:space_uuid/*");
    const space_uuid = match?.params?.space_uuid ?? "";
    const researchWithHtmlCreateMutation = useResearchWidthHtmlCreate();
    const optionsQuery = useSelectBoxOptions();

    const [tabActive, setTabActive] = useState("summary");
    const [language, setLanguage] = useState("ko");
    const [languageOptions, setLanguageOptions] = useState([]);
    const [styledText, setStyledText] = useState([]);
    const [summaryText, setSummaryText] = useState("");
    const { setLoading } = useLoading();

    useEffect(() => {
        if (optionsQuery.data) {
            setLanguageOptions(optionsQuery.data.env_ai_language);
        }
    }, [optionsQuery.data]);

    useEffect(() => {
        if (props.papers.type === "searchPapers" && props.papers.list.length > 0) {
            convertToBibText(props.papers.list);
        } else if (props.papers.type === "references" && props.papers.list.length > 0) {
            setStyledText(
                props.papers.list.map((reference) => ({
                    citation_key: reference.bib_json.citation_key,
                    styled_text: reference.styled_text[0],
                })),
            );
        }
    }, [props.papers]);

    const convertToBibText = (papers) => {
        const references = papers.map((paper) => {
            return toBibJson(paper);
        });

        getConvertedPaper(space_uuid, references).then((res) => setStyledText(res));
    };

    const addResearch = () => {
        let research = { html_content: summaryText };
        if (props.papers.type === "searchPapers") {
            research["reference_bib_jsons"] = props.papers.list.map((paper) => toBibJson(paper));
        } else {
            research["references"] = props.papers.list;
        }
        researchWithHtmlCreateMutation.mutate(research);
    };

    //region: 논문 요약 SSE 처리
    const [sseRequestId, setSseRequestId] = useState(null);

    const queryClient = useQueryClient();
    const ssePaperSummarizeQueryKey = "ssePaperSummarize";

    const ssePaperSummarizeQuery = useQuery({
        queryKey: [ssePaperSummarizeQueryKey],
        queryFn: async ({ signal }) => {
            try {
                setLoading(true);

                const request_id = uuidv4();
                setSseRequestId(request_id);

                const response = await fetch(sseUrl(ssePaperSummarizeQueryKey), {
                    signal,
                    method: "POST",
                    headers: {
                        Authorization: `Token ${localStorage.getItem("token")}`,
                        "Content-Type": "application/json",
                        "X-Request-ID": request_id,
                    },
                    body: JSON.stringify({
                        papers:
                            props.papers.type === "searchPapers"
                                ? props.papers.list.map((paper) => ({
                                      title: paper?.title,
                                      abstract: paper?.abstract,
                                      year: paper?.year,
                                  }))
                                : props.papers.list.map((paper) => ({
                                      title: paper?.bib_json?.title,
                                      abstract: paper?.bib_json?.abstract,
                                      year: paper?.bib_json?.year,
                                  })),
                        language: language,
                    }),
                });
                setLoading(false);

                if (!response.ok) {
                    throw new Error("Stream connection failed");
                }

                const reader = response.body?.getReader();
                if (!reader) {
                    throw new Error("Stream reader not available");
                }

                const decoder = new TextDecoder();
                while (true) {
                    const { value, done } = await reader.read();

                    if (done) {
                        break;
                    }

                    const dataRecv = decoder
                        .decode(value, { stream: true })
                        .replace("[", `<span style="font-weight: 700;">[`)
                        .replace("]", `]</span>`);
                    setSummaryText((prev) => {
                        return prev + dataRecv;
                    });
                }
            } catch (error) {
                throw error;
            }
        },
        enabled: false,
        retry: false,
        onError: (error) => {
            console.log("error", error);
            Alert("warn", "에러", "요약문을 생성하지 못했습니다.");
        },
    });

    const startSummary = () => {
        setSummaryText("");
        ssePaperSummarizeQuery.refetch();
    };

    const stopSummary = async () => {
        queryClient.cancelQueries({ queryKey: [ssePaperSummarizeQueryKey] });
        await fetch(sseUrl(ssePaperSummarizeQueryKey), {
            method: "PUT",
            headers: {
                Authorization: `Token ${localStorage.getItem("token")}`,
                "Content-Type": "application/json",
                "X-Request-ID": sseRequestId,
            },
            body: JSON.stringify({}),
        });
    };

    const stripSpans = (html) => {
        return html.replace(/<span[^>]*>(\[\d+\])<\/span>/g, "$1");
    };

    const renderSummaryText = () => {
        return stripSpans(summaryText)
            .split(/(\[\d+\])/g)
            .map((chunk, idx) => {
                const match = chunk.match(/\[(\d+)\]/);
                if (match) {
                    const index = match[1];
                    const paper = props.papers.list[Number(index) - 1]; // [1]은 0번째
                    let reference;
                    if (paper.id) {
                        reference = paper.bib_json;
                    } else {
                        reference = paper;
                    }
                    return (
                        <ReferenceLayer key={idx} reference={reference}>
                            [{index}]
                        </ReferenceLayer>
                    );
                } else {
                    return <span key={idx}>{chunk}</span>;
                }
            });
    };

    return (
        <Container>
            <ContentsWrap>
                <ContentsSection>
                    <TabSection>
                        <TabButton onClick={() => setTabActive("summary")} active={tabActive === "summary"}>
                            <TabButtonText isTabActive={tabActive === "summary"}>요약</TabButtonText>
                        </TabButton>
                        <TabButton onClick={() => setTabActive("papers")} active={tabActive === "papers"}>
                            <TabButtonText isTabActive={tabActive === "papers"}>출처</TabButtonText>
                        </TabButton>
                    </TabSection>
                    {tabActive === "summary" ? (
                        summaryText ? (
                            <ItemView key={"related-view"} active={true}>
                                <RelatedResearchView>
                                    <RelResearchView>
                                        <RelParagraphWrap>
                                            <RelParagraph>
                                                {renderSummaryText()}
                                                {ssePaperSummarizeQuery.isFetching && <AiCursor />}
                                            </RelParagraph>
                                        </RelParagraphWrap>
                                    </RelResearchView>
                                    <RelResearchViewFooter>
                                        {ssePaperSummarizeQuery.isFetching ? (
                                            <Button
                                                width={"120px"}
                                                height={"32px"}
                                                bgColor={"var(--color-Button2)"}
                                                hoverBgColor={"var(--color-ButtonHover2)"}
                                                fontColor={"var(--color-White)"}
                                                buttonText={"작성 중지"}
                                                onClick={() => {
                                                    stopSummary();
                                                }}
                                            >
                                                <Icon name={"stop"} color={"var(--color-White)"} />
                                            </Button>
                                        ) : (
                                            <>
                                                <Button
                                                    onlyText
                                                    margin={"8px"}
                                                    height={"32px"}
                                                    width={"120px"}
                                                    bgColor={"var(--color-Button2)"}
                                                    hoverBgColor={"var(--color-ButtonHover2)"}
                                                    fontColor={"var(--color-White)"}
                                                    buttonText={"연구노트로 추가"}
                                                    onClick={() => {
                                                        addResearch();
                                                    }}
                                                />
                                                <Button
                                                    onlyText
                                                    margin={"8px"}
                                                    height={"32px"}
                                                    width={"80px"}
                                                    bgColor={"var(--color-Button1)"}
                                                    hoverBgColor={"var(--color-ButtonHover1)"}
                                                    fontColor={"var(--color-White)"}
                                                    buttonText={"다시 작성"}
                                                    onClick={() => {
                                                        startSummary();
                                                    }}
                                                />
                                            </>
                                        )}
                                        <Button
                                            onlyText
                                            margin={"8px"}
                                            height={"32px"}
                                            width={"80px"}
                                            bgColor={"var(--color-Button4)"}
                                            hoverBgColor={"var(--color-ButtonHover4)"}
                                            fontColor={"var(--color-Black)"}
                                            buttonText={"닫기"}
                                            onClick={() => {
                                                stopSummary();
                                                props.exitModal();
                                            }}
                                        />
                                    </RelResearchViewFooter>
                                </RelatedResearchView>
                            </ItemView>
                        ) : (
                            <AnalysisView>
                                <AnalysisDescText>요약을 작성할 언어를 선택해주세요.</AnalysisDescText>
                                <ButtonSection>
                                    <SelectBox
                                        width="100px"
                                        height="32px"
                                        placeholder="언어 선택"
                                        items={languageOptions}
                                        value={language}
                                        onChange={(e) => setLanguage(e)}
                                    />
                                    <Button
                                        onlyText
                                        margin={"0 0 0 8px"}
                                        height={"32px"}
                                        bgColor={"var(--color-Button2)"}
                                        hoverBgColor={"var(--color-ButtonHover2)"}
                                        fontColor={"var(--color-White)"}
                                        buttonText={"요약하기"}
                                        onClick={startSummary}
                                        disabled={ssePaperSummarizeQuery.isFetching}
                                    />
                                </ButtonSection>
                            </AnalysisView>
                        )
                    ) : (
                        <ItemView>
                            <TabViewSection>
                                <ReferenceView>
                                    <ReferenceListWrap>
                                        {styledText.map((text) => (
                                            <RefListItemWrap key={text.citation_key}>
                                                <RefCitationText>{text.styled_text}</RefCitationText>
                                            </RefListItemWrap>
                                        ))}
                                    </ReferenceListWrap>
                                </ReferenceView>
                            </TabViewSection>
                        </ItemView>
                    )}
                </ContentsSection>
            </ContentsWrap>
        </Container>
    );
}

export default ReferenceSummaryModal;
